import React, { useEffect, useMemo, useState } from "react";
import styles from "./SupportTeamPermissionsTab.module.scss";
import Axios from "../../../../../_services/caller.service";
import { toast } from "react-toastify";
import { MdBlock, MdVisibility, MdEdit } from "react-icons/md";

const SupportTeamPermissionsTab = () => {
  const [users, setUsers] = useState([]);
  const [levels, setLevels] = useState([]);
  const [permissionsData, setPermissionsData] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState("");
  const [overrides, setOverrides] = useState({}); // { actionKey: access_level }
  const [effectivePermissions, setEffectivePermissions] = useState({}); // résultat de getEffectivePermissions

  // 1) Charger la liste initiale des utilisateurs “support”
  const fetchInitialUsers = async () => {
    try {
      const res = await Axios.get("/permissionsUsers?role=support");
      setUsers(res.data);
    } catch (err) {
      console.error("Erreur chargement utilisateurs :", err);
    }
  };

  // 2) Charger modules/actions + niveaux autorisés pour “support”
  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [modsRes, levelsRes] = await Promise.all([
          Axios.get("/getAllModulesWithActions?role=support"),
          Axios.get("/getAllLevelsWithAccess?role=support"),
        ]);
        setPermissionsData(modsRes.data);
        setLevels(levelsRes.data);
      } catch (err) {
        console.error("Erreur chargement permissions :", err);
      }
    };

    fetchAll();
    fetchInitialUsers();
  }, []);

  // Fonction extraite pour (re)charger les effectivePermissions
  const fetchEffectivePermissions = async (userId) => {
    if (!userId) {
      setEffectivePermissions({});
      return;
    }

    try {
      const res = await Axios.get(`/getEffectivePermissions/${userId}`);
      setEffectivePermissions(res.data.permissions || {});
    } catch (err) {
      console.error("Erreur chargement permissions effectives :", err);
      setEffectivePermissions({});
    }
  };

  // 3) Dès que selectedUserId change, on récupère ses overrides + permissions effectives
  useEffect(() => {
    if (!selectedUserId) return;

    // Charger overrides
    const fetchOverrides = async () => {
      try {
        const res = await Axios.get(`/getUserOverrides/${selectedUserId}`);
        setOverrides(res.data || {});
      } catch (err) {
        console.error("Erreur chargement overrides utilisateur :", err);
        setOverrides({});
      }
    };

    fetchOverrides();
    fetchEffectivePermissions(selectedUserId);
  }, [selectedUserId]);

  const selectedUser = useMemo(
    () => users.find((u) => u.id === selectedUserId),
    [selectedUserId, users]
  );

  // 4) Lorsque l’utilisateur change dans le <select> ou sur la carte, on met à jour selectedUserId
  const handleUserChange = (e) => {
    const userId = parseInt(e.target.value);
    setSelectedUserId(userId);
    // Les effets associés (overrides + effectivePermissions) se déclencheront dans useEffect
  };

  // 5) Modifier le niveau de support localement (avant d’envoyer en base)
  const handleSupportLevelChange = (e) => {
    const levelId = parseInt(e.target.value);
    setUsers((prev) =>
      prev.map((u) =>
        u.id === selectedUserId ? { ...u, supportLevelId: levelId } : u
      )
    );
  };

  // 6) Sauvegarde du niveau de support (POST vers backend)
  const handleSaveSupportLevel = async () => {
    if (!selectedUser) return;
    try {
      await Axios.post("/updateUserSupportLevel", {
        userId: selectedUser.id,
        supportLevelId: selectedUser.supportLevelId,
      });
      toast.success("💾 Niveau de support enregistré !");
      // Après avoir changé le niveau, on recommence un fetch des permissions effectives
      fetchEffectivePermissions(selectedUser.id);
    } catch (err) {
      toast.error("Erreur lors de l’enregistrement du niveau");
    }
  };

  // 7) Activation/désactivation d’un utilisateur
  const toggleUserActive = async (userId, currentActive) => {
    try {
      await Axios.put("/toggleUserActive", {
        userId,
        active: !currentActive,
      });
      setUsers((prev) =>
        prev.map((u) =>
          u.id === userId ? { ...u, active: !currentActive } : u
        )
      );
      toast.success("🔄 Statut utilisateur mis à jour !");
    } catch (err) {
      console.error("Erreur lors du changement de statut :", err);
      toast.error("Erreur serveur lors de l’activation/désactivation");
    }
  };

  // 8) Lorsqu’on coche/décoche une case, on enregistre l’override puis on relance le fetch des effectivePermissions
  const handleCheckboxToggle = (actionKey, level) => {
    // On construit le nouvel objet overrides (avant envoi)
    setOverrides((prev) => {
      const current = prev[actionKey];
      let newOverrides;

      if (current === level) {
        // Si on reclique sur la même case, on supprime l’override
        const { [actionKey]: _, ...rest } = prev;
        newOverrides = rest;
      } else {
        newOverrides = { ...prev, [actionKey]: level };
      }

      // Envoi immédiat vers le backend
      Axios.post("/saveUserOverrides", {
        userId: selectedUserId,
        overrides: newOverrides,
      })
        .then(() => {
          toast.success("✅ Override enregistré");
          // Après avoir enregistré, on met à jour effectivePermissions depuis l’API
          fetchEffectivePermissions(selectedUserId);
        })
        .catch((err) => {
          console.error("Erreur saveUserOverrides :", err);
          toast.error("Erreur enregistrement override");
        });

      return newOverrides;
    });
  };

  return (
    <div>
      {/* Ligne utilisateur + niveau + bouton enregistrer */}
      <div className={styles.selectRow}>
        <div>
          <label>👤 Utilisateur :</label>
          <select value={selectedUserId} onChange={handleUserChange}>
            <option value="">-- Choisir un utilisateur --</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label>📁 Support :</label>
          <select
            value={selectedUser?.supportLevelId || ""}
            onChange={handleSupportLevelChange}
          >
          
            {levels.map((lvl) => (
              <option key={lvl.id} value={lvl.id}>
                {lvl.name}
              </option>
            ))}
          </select>
        </div>

        <button onClick={handleSaveSupportLevel}>💾 Enregistrer niveau</button>
      </div>

      {/* Cartes des utilisateurs */}
      <h3 className={styles.sectionTitle}>👥 Liste des utilisateurs</h3>
      <div className={styles.userCards}>
        {users.map((user) => {
          const isSelected = user.id === selectedUserId;
          const support = levels.find((lvl) => lvl.id === user.supportLevelId);

          return (
            <div
              key={user.id}
              className={`${styles.userCard} ${
                isSelected ? styles.selected : ""
              } ${!user.active ? styles.inactive : ""}`}
              onClick={() => handleUserChange({ target: { value: user.id } })}
            >
              <h4>{user.name}</h4>
              <p>📁 Support : {support?.name || "Inconnu"}</p>
              <p>📶 Accès : {user.active ? "✅ Actif" : "❌ Inactif"}</p>
              <button
                className={styles.toggleActiveBtn}
                onClick={(e) => {
                  e.stopPropagation();
                  toggleUserActive(user.id, user.active);
                }}
              >
                {user.active ? "Désactiver" : "Activer"}
              </button>
            </div>
          );
        })}
      </div>

      {/* Permissions (affichées uniquement si un utilisateur est sélectionné) */}
      {selectedUser && (
        <div className={styles.permissionsPanel}>
          {permissionsData.map((mod) => (
            <div key={mod.id} className={styles.moduleCard}>
              <h3>{mod.name}</h3>
              <table className={styles.permissionsTable}>
                <thead>
                  <tr>
                    <th>🔧 Action</th>
                    <th>Description</th>
                    <th>Permissions</th>
                    <th>
                      <MdBlock className={styles.badgeNone} />
                      <br />
                      <span>Aucun</span>
                    </th>
                    <th>
                      <MdEdit className={styles.badgeFull} />
                      <br />
                      <span>Lecture/Écriture</span>
                    </th>
                    <th>
                      <MdVisibility className={styles.badgeRead} />
                      <br />
                      <span>Lecture</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {mod.actions.map((action) => {
                    // On lit directement effectivePermissions pour cet action
                    const effectiveLevel = effectivePermissions[action.key];
                    const overrideVal = overrides[action.key];

                    return (
                      <tr key={action.key}>
                        <td>{action.key}</td>
                        <td>{action.description || "-"}</td>
                        <td className={styles.previewCell}>
                          {/* On affiche l’icône correspondant au niveau effectif */}
                          {effectiveLevel === 0 && (
                            <>
                              <MdBlock className={styles.badgeNone} />
                              <span>Aucun</span>
                            </>
                          )}
                          {effectiveLevel === 2 && (
                            <>
                              <MdEdit className={styles.badgeFull} />
                              <span>Lecture/Écriture</span>
                            </>
                          )}
                          {effectiveLevel === 1 && (
                            <>
                              <MdVisibility className={styles.badgeRead} />
                              <span>Lecture</span>
                            </>
                          )}
                        </td>

                        {/* Trois cases à cocher pour override : 0, 2, 1 */}
                        {[0, 2, 1].map((val) => (
                          <td key={val}>
                            <input
                              type="checkbox"
                              checked={overrideVal === val}
                              onChange={() =>
                                handleCheckboxToggle(action.key, val)
                              }
                            />
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SupportTeamPermissionsTab;
